import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../hooks/useAuthCompat";
import { useApi } from "../hooks/useApi";

const TABS = ["Books", "Listening", "Learning", "Saved", "Membership"];

export default function Library() {
  const api = useApi();
  const { isSignedIn } = useUser();
  const [tab, setTab] = useState("Books");
  const { data, isLoading } = useQuery({ queryKey: ["library-home"], queryFn: async () => (await api.get("/library")).data, enabled: !!isSignedIn });

  if (!isSignedIn) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="library-signed-out">
        <p className="text-white/60">Sign in to view your personal library.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-16" data-testid="library-page">
      <p className="text-xs uppercase tracking-[0.2em] text-textSecondary mb-2">My Library</p>
      <h1 className="font-display text-display-md md:text-display-lg font-medium mb-10">Your Progress, in One Place</h1>

      <div className="flex gap-6 border-b border-white/10 mb-8 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`library-tab-${t.toLowerCase()}`} onClick={() => setTab(t)} className={`pb-3 text-sm uppercase tracking-wide whitespace-nowrap ${tab === t ? "text-white border-b-2 border-white" : "text-white/70"}`}>
            {t}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-white/70">Loading your library…</p>}

      {tab === "Books" && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
          {data?.books?.length ? data.books.map((b) => (
            <Link key={b.id} to={`/reader/${b.id}`} data-testid={`library-book-${b.id}`} className="group">
              <img src={b.cover_image} alt="" className="w-full aspect-square object-cover group-hover:opacity-80 transition-opacity duration-200" />
              <p className="text-xs mt-2">{b.title}</p>
              <span className="text-[10px] text-brandAccent uppercase">{b.progress ? "Continue" : "Open"}</span>
            </Link>
          )) : <p className="text-white/70 text-sm">No owned books yet. <Link to="/books" className="underline">Browse the bookstore</Link>.</p>}
        </div>
      )}

      {tab === "Listening" && (
        <div className="space-y-3">
          {data?.listening?.episodes?.length ? data.listening.episodes.map((e) => (
            <Link key={e.id} to={`/listen/${e.id}`} data-testid={`library-episode-${e.id}`} className="flex gap-4 items-center bg-surface border border-border p-3 hover:border-white/30">
              <img src={e.image_url} alt="" className="w-12 h-12 object-cover" />
              <p className="text-sm">{e.title}</p>
            </Link>
          )) : <p className="text-white/70 text-sm">No listening history yet. <Link to="/listen" className="underline">Explore episodes</Link>.</p>}
        </div>
      )}

      {tab === "Learning" && (
        <div>
          {data?.learning?.enrolled ? (
            <div>
              <p className="text-white/60 text-sm mb-4">{data.learning.lesson_progress.length} lesson(s) tracked.</p>
              <Link to="/learn" className="underline text-brandAccent text-sm">Continue Learning →</Link>
            </div>
          ) : <p className="text-white/70 text-sm">Not enrolled yet. <Link to="/learn" className="underline">Explore The Muscle School</Link>.</p>}
        </div>
      )}

      {tab === "Saved" && (
        <div className="space-y-2">
          {data?.saved_actions?.length ? data.saved_actions.map((s) => (
            <div key={s.id} data-testid={`library-saved-${s.id}`} className="bg-surface border border-border p-3 text-sm">{s.title}</div>
          )) : <p className="text-white/70 text-sm">Nothing saved yet. The AI Guide can save takeaways here.</p>}
        </div>
      )}

      {tab === "Membership" && (
        <div>
          {data?.membership ? (
            <div className="bg-surface border border-border p-6 max-w-md" data-testid="library-membership-card">
              <p className="text-sm uppercase tracking-wide text-brandAccent">{data.membership.product_type.replace("_", " ")}</p>
              <p className="text-white/75 text-xs mt-2">Status: {data.membership.status}</p>
            </div>
          ) : <p className="text-white/70 text-sm">No active membership. <Link to="/gold" className="underline">Explore Gold Vault</Link>.</p>}
        </div>
      )}
    </div>
  );
}
